import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Space, message, Row, Col, Checkbox, DatePicker, Segmented, Switch } from "antd";
import { PlusOutlined, InboxOutlined, MailOutlined, FormOutlined } from "@ant-design/icons";
import { API_CONFIG } from "./config";
import { apiFetch } from "../utils/apiFetch";

const { Option } = Select;

const statusOptions = ["New", "Contacted", "Qualified", "Disqualified", "Converted"];
const categoryOptions = ["potential", "bankable"];

const statusTag = (status) => {
  const color = status === "Converted" ? "gold" : status === "Disqualified" ? "red" : "blue";
  return <Tag color={color}>{status}</Tag>;
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [convertForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("leads.viewMode");
    return saved === "table" || saved === "cards" ? saved : "cards";
  });
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("leads.isDark");
    return saved === null ? true : saved === "true";
  });
  const useDarkStyles = true;
  const [filters, setFilters] = useState({ status: [], category: [], source: [] });

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(API_CONFIG.leadsListUrl, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load leads");
      setLeads(data.leads || []);
    } catch (err) {
      message.error(err.message || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    localStorage.setItem("leads.viewMode", viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem("leads.isDark", isDark);
  }, [isDark]);

  const openNewLead = (source) => {
    form.resetFields();
    form.setFieldsValue({ source });
    setOpen(true);
  };

  const handleSave = async (values) => {
    try {
      const res = await apiFetch(API_CONFIG.leadsSaveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to save lead");
      message.success("Lead saved");
      setOpen(false);
      fetchLeads();
    } catch (err) {
      message.error(err.message || "Failed to save lead");
    }
  };

  const updateLeadStatus = async (lead, status) => {
    await apiFetch(API_CONFIG.leadsStatusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lead.id, status }),
    });
    fetchLeads();
  };

  const updateLeadCategory = async (lead, category) => {
    await apiFetch(API_CONFIG.leadsStatusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lead.id, category }),
    });
    fetchLeads();
  };

  const openComment = (lead) => {
    setSelectedLead(lead);
    commentForm.resetFields();
    setCommentOpen(true);
  };

  const submitComment = async (values) => {
    await apiFetch(API_CONFIG.leadsCommentUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: selectedLead.id, text: values.comment }),
    });
    setCommentOpen(false);
    fetchLeads();
  };

  const openConvert = (lead) => {
    setSelectedLead(lead);
    convertForm.resetFields();
    setConvertOpen(true);
  };

  const submitConvert = async (values) => {
    const payload = { ...values, date: values.date?.toISOString?.() || values.date };
    await apiFetch(API_CONFIG.leadsConvertUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: selectedLead.id, booking: payload }),
    });
    setConvertOpen(false);
    fetchLeads();
  };

  const columns = [
    { title: "Lead ID", dataIndex: "id", key: "id", width: 120 },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "Source", dataIndex: "source", key: "source", width: 100 },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (_, lead) => (
        <Select value={lead.category} onChange={(v) => updateLeadCategory(lead, v)} size="small">
          {categoryOptions.map((c) => (
            <Option key={c} value={c}>{c}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, lead) => (
        <Space>
          {statusTag(lead.status)}
          <Select value={lead.status} onChange={(v) => updateLeadStatus(lead, v)} size="small">
            {statusOptions.map((s) => (
              <Option key={s} value={s}>{s}</Option>
            ))}
          </Select>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, lead) => (
        <Space>
          <Button size="small" onClick={() => openComment(lead)}>Comment</Button>
          <Button size="small" type="primary" onClick={() => openConvert(lead)}>Convert</Button>
        </Space>
      ),
    },
  ];

  const stats = useMemo(() => {
    const total = leads.length;
    const bankable = leads.filter((l) => l.category === "bankable").length;
    const potential = leads.filter((l) => l.category === "potential").length;
    return { total, bankable, potential };
  }, [leads]);

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const values = new Set(prev[key] || []);
      if (values.has(value)) values.delete(value);
      else values.add(value);
      return { ...prev, [key]: Array.from(values) };
    });
  };

  const clearFilters = () => setFilters({ status: [], category: [], source: [] });

  const filteredLeads = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return leads.filter((lead) => {
      const matchesSearch = !term ||
        [lead.name, lead.email, lead.phone, lead.id].filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
      const matchesStatus = filters.status.length === 0 || filters.status.includes(lead.status);
      const matchesCategory = filters.category.length === 0 || filters.category.includes(lead.category);
      const matchesSource = filters.source.length === 0 || filters.source.includes(lead.source);
      return matchesSearch && matchesStatus && matchesCategory && matchesSource;
    });
  }, [leads, searchTerm, filters]);

  const cardInitials = (name) => {
    const parts = String(name || "").trim().split(" ");
    return (parts[0]?.[0] || "L").toUpperCase();
  };

  return (
    <div
      className={`appverse-content table-container tabled ${useDarkStyles ? "luxe-dark" : "luxe-light"}`}
      style={{
        background: isDark ? "#000" : "#fff",
        minHeight: "100vh",
        marginLeft: "var(--sidenav-width, 88px)",
        padding: "0 20px 0 12px",
        position: "relative",
        zIndex: 1002,
      }}
    >
      <Row gutter={0}>
        <Col xs={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24 luxe-dark-bg"
            style={{ margin: 0, position: "relative", zIndex: 1003 }}
            title={<span className="luxe-card-title">Leads Management</span>}
            extra={
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div className="mode-toggle">
                  <Segmented
                    options={[
                      { label: "Cards", value: "cards" },
                      { label: "Table", value: "table" },
                    ]}
                    value={viewMode}
                    onChange={(v) => setViewMode(v)}
                    defaultValue="cards"
                  />
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, paddingLeft: 4 }}>
                  <span style={{ color: "#fff8dc", fontWeight: 600 }}>Dark</span>
                  <Switch checked={isDark} onChange={(v) => setIsDark(v)} />
                </div>
              </div>
            }
            bodyStyle={{ background: "#000", borderRadius: "18px" }}
          >
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="kpi-card">
                <div className="kpi-title">Total Leads</div>
                <div className="kpi-value">{stats.total}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Bankable</div>
                <div className="kpi-value">{stats.bankable}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-title">Potential</div>
                <div className="kpi-value">{stats.potential}</div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <Button icon={<FormOutlined />} className="luxe-btn" onClick={() => openNewLead("web")}>Web Form</Button>
              <Button icon={<MailOutlined />} className="luxe-btn" onClick={() => openNewLead("email")}>Incoming Email</Button>
              <Button icon={<InboxOutlined />} className="luxe-btn" onClick={() => openNewLead("excel")}>Import Excel</Button>
              <Button type="primary" icon={<PlusOutlined />} className="luxe-btn" onClick={() => openNewLead("web")}>New Lead</Button>
            </div>

            <Input.Search
              placeholder="Search leads by name, email, phone, or ID"
              onChange={(e) => setSearchTerm(e.target.value)}
              value={searchTerm}
              className="luxe-search"
              enterButton={<Button className="luxe-search-btn">Search</Button>}
            />

            <div className="filter-chips">
              <span className="chip-filter">Status</span>
              {statusOptions.map((status) => (
                <span
                  key={status}
                  className={`chip-filter ${filters.status.includes(status) ? "active" : ""}`}
                  onClick={() => toggleFilter("status", status)}
                >
                  {status}
                </span>
              ))}
              <span className="chip-filter">Category</span>
              {categoryOptions.map((category) => (
                <span
                  key={category}
                  className={`chip-filter ${filters.category.includes(category) ? "active" : ""}`}
                  onClick={() => toggleFilter("category", category)}
                >
                  {category}
                </span>
              ))}
              <span className="chip-filter">Source</span>
              {["web", "email", "excel"].map((source) => (
                <span
                  key={source}
                  className={`chip-filter ${filters.source.includes(source) ? "active" : ""}`}
                  onClick={() => toggleFilter("source", source)}
                >
                  {source}
                </span>
              ))}
              {(filters.status.length || filters.category.length || filters.source.length) > 0 && (
                <span className="chip-filter clear" onClick={clearFilters}>Clear</span>
              )}
            </div>

            {viewMode === "cards" ? (
              <div className="indigent-grid" style={{ marginTop: 20 }}>
                {filteredLeads.map((lead) => (
                  <div key={lead.id} className="indigent-card">
                    <div className="indigent-head">
                      <div className="indigent-avatar">{cardInitials(lead.name)}</div>
                      <div>
                        <div className="indigent-name">{lead.name || "Unknown"}</div>
                        <div className="indigent-meta">{lead.id} · {lead.email || "No email"}</div>
                      </div>
                    </div>
                    <div className="indigent-tags">
                      <span className="chip dark">Status: {lead.status}</span>
                      <span className="chip dark">Category: {lead.category}</span>
                      <span className="chip dark">Source: {lead.source}</span>
                      <span className="chip dark">Phone: {lead.phone || "-"}</span>
                    </div>
                    <div className="indigent-actions">
                      <Button className="luxe-btn" onClick={() => openComment(lead)}>Comment</Button>
                      <Button className="luxe-btn" onClick={() => openConvert(lead)}>Convert</Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table
                rowKey="id"
                loading={loading}
                dataSource={filteredLeads}
                columns={columns}
                className="luxe-table"
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="New Lead" open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} okText="Save">
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Enter name" }]}>
            <Input placeholder="Lead name" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input placeholder="Email address" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="Phone number" />
          </Form.Item>
          <Form.Item name="source" label="Source" rules={[{ required: true }]}>
            <Select>
              <Option value="web">Web Form</Option>
              <Option value="email">Email</Option>
              <Option value="excel">Excel Import</Option>
            </Select>
          </Form.Item>
          <Form.Item name="category" label="Category" initialValue="potential">
            <Select>
              <Option value="potential">Potential</Option>
              <Option value="bankable">Bankable</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="New">
            <Select>
              {statusOptions.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Add Comment" open={commentOpen} onCancel={() => setCommentOpen(false)} onOk={() => commentForm.submit()} okText="Save">
        <Form form={commentForm} layout="vertical" onFinish={submitComment}>
          <Form.Item name="comment" label="Comment" rules={[{ required: true, message: "Enter a comment" }]}>
            <Input.TextArea rows={4} placeholder="Add a note about this lead" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Convert Lead to Client" open={convertOpen} onCancel={() => setConvertOpen(false)} onOk={() => convertForm.submit()} okText="Convert">
        <Form form={convertForm} layout="vertical" onFinish={submitConvert}>
          <Form.Item name="service" label="Service" rules={[{ required: true, message: "Enter service" }]}>
            <Input placeholder="Service / product" />
          </Form.Item>
          <Form.Item name="date" label="Booking Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="termsAccepted" valuePropName="checked">
            <Checkbox>I confirm terms and conditions</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
